<!--
  Распорядок дня военнослужащих на сутки (боевой расчёт).
  По ТЗ 03.09.2026 (встреча по Сакшы):
    * Строки — личный состав, столбцы — часы (шаг 30 минут), сутки 20:00 → 20:00.
    * Шапка часа объединена в один квадрат (colspan=2 по 30 мин), без «:00/:30».
    * 17 активностей с приоритетами: служба > сон > остальное.
    * Сон — 2 блока по 4 часа = 8 часов обязательно. Индикатор недосыпа в ФИО.
    * После наряда — 1 час личного времени, затем сон.
    * Ручная правка: клик по ячейке → выбор активности.
    * Persist: сохраняется в DailySchedule (chpo, user, slots_json).
    * Печать A4 landscape, Excel через ExcelJS с ч/б patterns.
-->

<template>
  <section class="daily">
    <header class="daily__header no-print">
      <div>
        <h2 class="daily__title">{{ $t('chpo.daily.title') }}</h2>
        <div class="daily__subtitle">
          {{ $t('chpo.daily.subtitle', { day: dayLabel, date: dateISO }) }}
        </div>
      </div>
      <div class="daily__toolbar">
        <button class="btn btn--secondary" @click="$router.back()">{{ $t('common.back') }}</button>
        <button class="btn btn--secondary" @click="regenerate" :disabled="loading">↻ {{ $t('chpo.daily.regenerate') }}</button>
        <button class="btn btn--secondary" @click="saveAll" :disabled="saving">💾 {{ saving ? $t('common.saving') : $t('common.save') }}</button>
        <button class="btn btn--primary" @click="printPage">🖨 {{ $t('chpo.daily.printBtn') }}</button>
        <button class="btn btn--primary" @click="exportExcel">⬇ {{ $t('chpo.daily.excelBtn') }}</button>
      </div>
    </header>

    <div class="daily__print-header print-only">
      <div class="print__unit">{{ $t('chpo.daily.printUnit') }}</div>
      <div class="print__title">{{ $t('chpo.daily.printTitle') }}</div>
      <div class="print__date">{{ dayLabel }}, {{ dateISO }}</div>
    </div>

    <!-- Легенда 17 активностей — эмодзи + подпись, без фонов -->
    <div class="daily__legend">
      <div v-for="(cfg, key) in ACTIVITIES" :key="key" class="legend">
        <!-- По ТЗ 24.09.2026: сон = 1 полоса, служба = 2 полосы (условные
             знаки, как в таблице); остальные активности — эмодзи как раньше. -->
        <span v-if="key === 'sleep'" class="legend__cell legend__cell--stripe legend__cell--sleep" aria-hidden="true"></span>
        <span v-else-if="key === 'duty'" class="legend__cell legend__cell--stripe legend__cell--duty" aria-hidden="true"></span>
        <span v-else class="legend__cell">{{ cfg.icon }}</span>
        <span class="legend__label">{{ $t(cfg.i18n) }}</span>
      </div>
    </div>

    <div v-if="loading" class="daily__loading">{{ $t('common.loadingData') }}</div>
    <div v-else-if="!hasData" class="daily__empty">
      <div>{{ $t('chpo.daily.emptyTitle') }}</div>
      <div class="hint">{{ $t('chpo.daily.emptyHintPrefix') }} <router-link to="/chpo/plan">{{ $t('chpo.daily.emptyHintLink') }}</router-link> {{ $t('chpo.daily.emptyHintSuffix') }}</div>
    </div>

    <div v-else class="daily__wrap">
      <table class="daily__table">
        <thead>
          <!-- Одна строка: 24 часовых заголовка, каждый colspan=2 -->
          <tr>
            <th rowspan="1" class="daily__col--num">{{ $t('chpo.daily.colNum') }}</th>
            <th rowspan="1" class="daily__col--fio">{{ $t('chpo.daily.colFio') }}</th>
            <th rowspan="1" class="daily__col--sleep">{{ $t('chpo.daily.colSleep') }}</th>
            <th v-for="h in hourCols" :key="h.key" class="daily__col--hour" :class="{ 'daily__col--hour-major': h.isMajor }" colspan="2">{{ h.label }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="p in personnel" :key="p.id">
            <td class="daily__col--num">{{ p.personal_number || '—' }}</td>
            <td class="daily__col--fio">
              <span>{{ p.short_name || p.full_name }}</span>
              <span v-if="sleepHours(p.id) < 8" class="daily__badge" :title="$t('chpo.daily.warnSleepShort', { h: sleepHours(p.id) })">⚠ {{ sleepHours(p.id) }}ч</span>
            </td>
            <td class="daily__col--sleep">{{ sleepHours(p.id) }}</td>
            <td
              v-for="slot in slots"
              :key="`${p.id}-${slot.idx}`"
              class="daily__cell"
              :class="cellClass(p, slot)"
              :style="cellStyle(p, slot)"
              :title="cellTitle(p, slot)"
              @click="onCellClick(p, slot, $event)"
            >
              <span class="daily__cell-icon">{{ cellIcon(p, slot) }}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Мини-редактор ячейки -->
    <div v-if="editor.open" class="cell-editor no-print" :style="{ top: editor.y + 'px', left: editor.x + 'px' }" @click.stop>
      <div class="cell-editor__title">
        {{ editor.person && (editor.person.short_name || editor.person.full_name) }} · {{ fmtHHMM(START_HOUR * 60 + editor.slotIdx * 30) }}
      </div>
      <div class="cell-editor__grid">
        <button
          v-for="(cfg, key) in ACTIVITIES"
          :key="key"
          class="cell-editor__opt"
          @click="setCellKind(key)"
        >{{ cfg.icon }} {{ $t(cfg.i18n) }}</button>
        <button class="cell-editor__opt cell-editor__opt--clear" @click="setCellKind(null)">✕ {{ $t('chpo.daily.clear') }}</button>
      </div>
      <div class="cell-editor__hint">{{ $t('chpo.daily.editorHint') }}</div>
    </div>
  </section>
</template>

<script>
import { checkOrCreateChpoByDate, computeChpoDate } from '@/api/chpo'
import { listBookUsers } from '@/api/bookUsers'
import { ensureTodayBookContext } from '@/api/book'
import { getActiveRoutineTemplate, listSchedules, upsertSchedule } from '@/api/dailyRoutine'
import {
  ACTIVITIES, SLOTS_PER_DAY, SLOT_MINUTES, START_HOUR,
  SLEEP_MIN_MINUTES, SLEEP_BLOCK_MINUTES,
  rangeAbs, slotAbsMinutes, fmtHHMM,
} from './dailySchedule.constants'

export default {
  name: 'DailyScheduleView',
  data() {
    return {
      loading: false,
      saving: false,
      dateISO: '',
      chpoId: null,
      personnel: [],
      // Map<user_id, Array<{kind, note, source}>>. Одна активность на слот.
      grid: new Map(),
      dirty: new Set(), // user_id-ы с ручными правками (требуют сохранения)
      editor: { open: false, x: 0, y: 0, person: null, slotIdx: -1 },
      ACTIVITIES,
      START_HOUR,
    }
  },
  computed: {
    hasData() { return this.personnel.length > 0 },

    dayLabel() {
      if (!this.dateISO) return ''
      try {
        const d = new Date(this.dateISO + 'T00:00:00')
        const locale = this.$i18n.locale === 'kz' ? 'kk-KZ' : 'ru-RU'
        return d.toLocaleDateString(locale, { weekday: 'long' })
      } catch { return '' }
    },
    weekdayNum() {
      // 0..6, пн=0
      if (!this.dateISO) return 0
      try {
        const d = new Date(this.dateISO + 'T00:00:00')
        const wd = d.getDay() // 0=вс..6=сб
        return wd === 0 ? 6 : wd - 1
      } catch { return 0 }
    },
    slots() {
      const out = []
      for (let i = 0; i < SLOTS_PER_DAY; i++) {
        const abs = slotAbsMinutes(i)
        out.push({ idx: i, absMin: abs, hh: Math.floor(abs / 60) % 24, mm: abs % 60 })
      }
      return out
    },
    hourCols() {
      const out = []
      for (let h = 0; h < 24; h++) {
        const absH = (START_HOUR + h) % 24
        out.push({ key: h, label: String(absH).padStart(2, '0'), isMajor: h > 0 && h % 4 === 0 })
      }
      return out
    },
  },
  async mounted() {
    document.addEventListener('click', this.closeEditor)
    await this.load()
  },
  beforeUnmount() {
    document.removeEventListener('click', this.closeEditor)
  },
  methods: {
    fmtHHMM,

    async load({ fresh = false } = {}) {
      this.loading = true
      try {
        this.dateISO = computeChpoDate()
        const ctx = await checkOrCreateChpoByDate(this.dateISO)
        this.chpoId = ctx?.id || null

        await ensureTodayBookContext()
        const bookUsers = await listBookUsers({ is_archived: false })
        const seen = new Set()
        this.personnel = (bookUsers || [])
          .filter(bu => {
            const uid = (bu.user && bu.user.id) || bu.user_id || bu.id
            if (!uid || seen.has(uid)) return false
            seen.add(uid); return true
          })
          .map(bu => {
            const u = bu.user || bu
            return {
              id: u.user_id || u.id || bu.user_id,
              personal_number: u.personal_number ?? bu.personal_number ?? '',
              full_name: u.full_name || bu.fio || bu.full_name || '',
              short_name: this.shortenName(u.full_name || bu.fio || bu.full_name || ''),
            }
          })
          .filter(x => x.id)

        this.grid = new Map()
        this.dirty = new Set()

        // 1. Есть ли сохранённое расписание для этой книги CHPO — грузим его.
        let saved = []
        if (this.chpoId && !fresh) {
          try { saved = await listSchedules({ chpo: this.chpoId }) } catch (_) { saved = [] }
        }
        const savedByUser = new Map()
        for (const s of (saved || [])) savedByUser.set(s.user, s.slots_json || [])

        // 2. Иначе — генерим на лету из CHPO+шаблон.
        const routineTemplate = await getActiveRoutineTemplate().catch(() => null)
        const orders = ctx?.orders_to_squads || []

        for (const p of this.personnel) {
          if (savedByUser.has(p.id) && savedByUser.get(p.id).length === SLOTS_PER_DAY) {
            // Загружаем 48-slot массив
            this.grid.set(p.id, this.rowFromJson(savedByUser.get(p.id)))
          } else {
            this.grid.set(p.id, this.generateRow(p.id, orders, routineTemplate))
          }
        }
      } catch (e) {
        console.error('DailySchedule: load failed', e)
      } finally {
        this.loading = false
      }
    },

    rowFromJson(json) {
      const row = new Array(SLOTS_PER_DAY)
      for (let i = 0; i < SLOTS_PER_DAY; i++) {
        const s = json[i] || {}
        row[i] = { kind: s.kind || null, note: s.note || '', source: s.source || 'saved' }
      }
      return row
    },
    rowToJson(row) {
      return row.map((s, i) => ({ i, kind: s.kind || null, note: s.note || '', source: s.source || 'auto' }))
    },
    emptyRow() {
      const arr = new Array(SLOTS_PER_DAY)
      for (let i = 0; i < SLOTS_PER_DAY; i++) arr[i] = { kind: null, note: '', source: 'auto' }
      return arr
    },

    /** Распределение по приоритету: применяем правила в порядке возрастания
     *  приоритета — верхние затираются нижними. */
    generateRow(userId, orders, template) {
      const row = this.emptyRow()

      const rules = []

      // A. Типовой распорядок (низший приоритет применения — идёт первым в списке rules,
      // но каждая ячейка проверяет priority ACTIVITIES[kind])
      if (template && Array.isArray(template.slots)) {
        for (const sl of template.slots) {
          if (sl.weekday !== -1 && sl.weekday !== this.weekdayNum) continue
          const r = rangeAbs(sl.start_time, sl.end_time)
          if (r) rules.push({ from: r[0], to: r[1], kind: sl.activity, note: sl.note || '', source: 'template' })
        }
      }

      // B. Наряды из CHPO — приоритет 100 (перекрывает всё)
      for (const sq of (orders || [])) {
        const ids = new Set()
        if (sq.chief) ids.add(sq.chief.id ?? sq.chief_id ?? sq.chief)
        if (sq.chief_id) ids.add(sq.chief_id)
        for (const u of (sq.users || [])) ids.add(u.id ?? u.user_id ?? u)
        if (!ids.has(userId)) continue
        const r = rangeAbs(sq.start_time, sq.end_time)
        if (!r) continue
        rules.push({ from: r[0], to: r[1], kind: 'duty', note: sq.squad_type || sq.order_type || '', source: 'plan' })

        // По ТЗ 09.09.2026: убрали forced 6ч сна после смены — из-за него
        // общий сон получался 11ч (шаблон 5ч + forced 6ч). Пусть недостающее
        // до 8ч добирает topUpSleep из свободных окон.
      }

      // Применяем: если новая активность имеет priority >= текущей — перезаписываем.
      for (const rule of rules) {
        const newPr = ACTIVITIES[rule.kind]?.priority ?? 0
        const first = Math.max(0, Math.floor(rule.from / SLOT_MINUTES))
        const last  = Math.min(SLOTS_PER_DAY, Math.ceil(rule.to / SLOT_MINUTES))
        for (let i = first; i < last; i++) {
          const curKind = row[i].kind
          const curPr = curKind ? (ACTIVITIES[curKind]?.priority ?? 0) : -1
          if (newPr >= curPr) row[i] = { kind: rule.kind, note: rule.note, source: rule.source }
        }
      }

      // Шаблонные приёмы пищи снимаем до расчёта сна: сон важнее, а приёмы
      // пищи всё равно переставляются на шаге F вокруг службы и сна.
      for (let i = 0; i < SLOTS_PER_DAY; i++) {
        if (row[i].kind === 'meal' && row[i].source === 'template') row[i] = { kind: null, note: '', source: 'auto' }
      }

      // E. Досып до 8ч, если суммарный сон меньше — добираем в свободных (без активности) слотах.
      this.topUpSleep(row)

      // F. Приёмы пищи: не теряем, а переносим в ближайшее свободное окно
      // с зазором 30 минут до/после наряда.
      const meals = rules.filter(r => r.kind === 'meal' && r.source === 'template')
      this.placeMeals(row, meals)

      return row
    },

    /** Приём пищи не должен пропадать из-за службы/сна, но и не должен им мешать.
     *  Снимаем шаблонные meal-слоты и расставляем каждый приём заново:
     *   - только в пустых ячейках (служба, сон, зарядка, расчёт не трогаются);
     *   - между приёмом и службой минимум MEAL_DUTY_GAP_SLOTS (30 мин) с обеих сторон;
     *   - между приёмами пищи минимум MEAL_MEAL_GAP_SLOTS (1,5 часа);
     *   - ищем ближайший к шаблонному времени старт (при равенстве — позже)
     *     в пределах MEAL_MAX_SHIFT_SLOTS; на каждом сдвиге сначала полная
     *     длительность, затем укороченный приём на 30 минут. */
    placeMeals(row, meals) {
      const MEAL_DUTY_GAP_SLOTS = 1
      const MEAL_MEAL_GAP_SLOTS = 3
      const MEAL_MAX_SHIFT_SLOTS = 12

      for (let i = 0; i < SLOTS_PER_DAY; i++) {
        if (row[i].kind === 'meal' && row[i].source === 'template') row[i] = { kind: null, note: '', source: 'auto' }
      }

      const fits = (start, len) => {
        if (start < 0 || start + len > SLOTS_PER_DAY) return false
        for (let k = start; k < start + len; k++) if (row[k].kind) return false
        for (let g = 1; g <= MEAL_DUTY_GAP_SLOTS; g++) {
          if (row[start - g]?.kind === 'duty') return false
          if (row[start + len - 1 + g]?.kind === 'duty') return false
        }
        for (let g = 1; g <= MEAL_MEAL_GAP_SLOTS; g++) {
          if (row[start - g]?.kind === 'meal' || row[start + len - 1 + g]?.kind === 'meal') return false
        }
        return true
      }

      for (const meal of meals) {
        const origStart = Math.max(0, Math.floor(meal.from / SLOT_MINUTES))
        const origLen = Math.max(1, Math.min(SLOTS_PER_DAY, Math.ceil(meal.to / SLOT_MINUTES)) - origStart)
        const lengths = origLen > 1 ? [origLen, 1] : [1]
        // Сначала в пределах MEAL_MAX_SHIFT_SLOTS, если не нашлось — по всем суткам
        // (например, после ночного наряда весь день занят сном).
        const findNear = (maxShift) => {
          for (let d = 0; d <= maxShift; d++) {
            for (const len of lengths) {
              if (fits(origStart + d, len)) return { start: origStart + d, len }
              if (d > 0 && fits(origStart - d, len)) return { start: origStart - d, len }
            }
          }
          return null
        }
        const placed = findNear(MEAL_MAX_SHIFT_SLOTS) || findNear(SLOTS_PER_DAY)
        if (!placed) continue
        for (let k = placed.start; k < placed.start + placed.len; k++) {
          row[k] = { kind: 'meal', note: meal.note, source: 'template' }
        }
      }
    },

    /** По ТЗ 09.09.2026: суммарный сон = 8 часов, и если он разбит нарядом
     *  на несколько блоков — каждый блок ровно 4 часа. Правила:
     *   0) Если наряд «съел» сон так, что от блока осталось меньше 4 часов —
     *      такой огрызок убираем: спать урывками смысла нет.
     *   1) Блоки sleep, примыкающие к duty, длиннее 4 часов обрезаем до 4 часов
     *      (оставляем более раннюю часть).
     *   2) Если суммарно всё ещё > 8 часов (шаблон дал слишком много) —
     *      обрезаем самые длинные блоки до нормы.
     *   3) Добираем недостающее в свободных окнах (kind=null) с зазором
     *      SLEEP_DUTY_GAP_SLOTS от наряда: если сна не осталось совсем — одним
     *      блоком 8 часов, иначе блоками по 4 часа. Предпочтительно сразу после
     *      наряда, затем — перед ним, затем — в любом подходящем окне.
     *   4) Если блоками не влезло — добиваем в самых длинных свободных окнах.
     *  Шаблонные pfz/rally при этом не трогаем. */
    topUpSleep(row) {
      const totalSlotsNeeded = SLEEP_MIN_MINUTES / SLOT_MINUTES // 16
      const blockSlots = SLEEP_BLOCK_MINUTES / SLOT_MINUTES     // 8
      const SLEEP_DUTY_GAP_SLOTS = 1                            // 30 мин

      const currentSleep = () => row.reduce((n, s) => n + (s.kind === 'sleep' ? 1 : 0), 0)
      const clear = (from, to) => { for (let k = from; k < to; k++) row[k] = { kind: null, note: '', source: 'auto' } }
      const sleepRuns = () => {
        const runs = []
        let i = 0
        while (i < SLOTS_PER_DAY) {
          if (row[i].kind !== 'sleep') { i++; continue }
          let j = i
          while (j < SLOTS_PER_DAY && row[j].kind === 'sleep') j++
          runs.push({ start: i, end: j, len: j - i })
          i = j
        }
        return runs
      }
      const touchesDuty = (r) => (r.start > 0 && row[r.start - 1].kind === 'duty')
        || (r.end < SLOTS_PER_DAY && row[r.end].kind === 'duty')

      // Шаг 0: огрызки сна < 4 часов, обрезанные нарядом, — убираем.
      for (const r of sleepRuns()) {
        if (touchesDuty(r) && r.len < blockSlots) clear(r.start, r.end)
      }

      // Шаг 1: блоки sleep, соседствующие с duty, режем до blockSlots.
      for (const r of sleepRuns()) {
        if (touchesDuty(r) && r.len > blockSlots) clear(r.start + blockSlots, r.end)
      }

      // Шаг 2: если всё ещё > нормы — обрезаем длинные блоки.
      let excess = currentSleep() - totalSlotsNeeded
      while (excess > 0) {
        const runs = sleepRuns()
        if (!runs.length) break
        const best = runs.reduce((a, b) => (b.len > a.len ? b : a))
        const trim = Math.min(excess, best.len)
        clear(best.end - trim, best.end)
        excess -= trim
      }

      // Шаг 3: переносим сон целыми блоками с зазором от наряда.
      const fits = (start, len) => {
        if (start < 0 || start + len > SLOTS_PER_DAY) return false
        for (let k = start; k < start + len; k++) if (row[k].kind) return false
        for (let g = 1; g <= SLEEP_DUTY_GAP_SLOTS; g++) {
          if (row[start - g]?.kind === 'duty' || row[start + len - 1 + g]?.kind === 'duty') return false
        }
        return true
      }
      const findPos = (len) => {
        const dutyEnds = [], dutyStarts = []
        for (let k = 0; k < SLOTS_PER_DAY; k++) {
          if (row[k].kind !== 'duty') continue
          if (k === 0 || row[k - 1].kind !== 'duty') dutyStarts.push(k)
          if (k === SLOTS_PER_DAY - 1 || row[k + 1].kind !== 'duty') dutyEnds.push(k + 1)
        }
        // Сразу после наряда (ближайшее подходящее начало).
        for (const e of dutyEnds) {
          for (let p = e + SLEEP_DUTY_GAP_SLOTS; p + len <= SLOTS_PER_DAY; p++) {
            if (row[p].kind === 'duty') break
            if (fits(p, len)) return p
          }
        }
        // Перед нарядом (как можно ближе к его началу).
        for (const st of dutyStarts) {
          for (let p = st - SLEEP_DUTY_GAP_SLOTS - len; p >= 0; p--) {
            if (row[p + len - 1]?.kind === 'duty') break
            if (fits(p, len)) return p
          }
        }
        // Любое подходящее окно.
        for (let p = 0; p + len <= SLOTS_PER_DAY; p++) if (fits(p, len)) return p
        return -1
      }
      const put = (start, len) => {
        for (let k = start; k < start + len; k++) row[k] = { kind: 'sleep', note: '', source: 'auto-sleep' }
      }

      if (currentSleep() === 0) {
        const p = findPos(totalSlotsNeeded)
        if (p >= 0) put(p, totalSlotsNeeded)
      }
      let guard = 0
      while (totalSlotsNeeded - currentSleep() >= blockSlots && guard++ < 3) {
        const p = findPos(blockSlots)
        if (p < 0) break
        put(p, blockSlots)
      }

      // Шаг 4: не влезло блоками — добиваем в самых длинных свободных окнах.
      let attempts = 0
      while (currentSleep() < totalSlotsNeeded && attempts++ < 5) {
        const runs = []
        let ii = 0
        while (ii < SLOTS_PER_DAY) {
          if (row[ii].kind) { ii++; continue }
          let jj = ii
          while (jj < SLOTS_PER_DAY && !row[jj].kind) jj++
          runs.push({ start: ii, len: jj - ii })
          ii = jj
        }
        if (!runs.length) break
        const best = runs.reduce((a, b) => (b.len > a.len ? b : a))
        const take = Math.min(best.len, totalSlotsNeeded - currentSleep(), blockSlots)
        if (take <= 0) break
        put(best.start, take)
      }
    },

    // Часы сна для пользователя (округлено до 0.5)
    sleepHours(userId) {
      const row = this.grid.get(userId)
      if (!row) return 0
      const slots = row.reduce((n, s) => n + (s.kind === 'sleep' ? 1 : 0), 0)
      return slots / 2
    },

    cellClass(p, slot) {
      const s = this.grid.get(p.id)?.[slot.idx]
      const row = this.grid.get(p.id) || []
      const nextKind = row[slot.idx + 1]?.kind
      const prevKind = row[slot.idx - 1]?.kind
      if (!s || !s.kind) return { 'is-half': slot.mm === 30 }
      // По ТЗ 22.09.2026: длинные блоки одного типа (сон, служба, любой другой)
      // должны выглядеть как единая полоса — is-continues-right/left убирают
      // границу между соседними ячейками одного kind.
      return {
        [`is-${s.kind}`]: true,
        'is-half': slot.mm === 30,
        'is-continues-right': nextKind === s.kind,
        'is-continues-left':  prevKind === s.kind,
      }
    },
    cellStyle() {
      // По ТЗ 03.09.2026: без цветного фона — только эмодзи в белой клетке.
      return {}
    },
    cellIcon(p, slot) {
      const s = this.grid.get(p.id)?.[slot.idx]
      if (!s || !s.kind) return ''
      // По ТЗ 24.09.2026: сон и служба показываются только полосами, без
      // эмодзи поверх — полосы самодостаточны как визуальный маркер.
      if (s.kind === 'sleep' || s.kind === 'duty') return ''
      const row = this.grid.get(p.id) || []
      const prev = row[slot.idx - 1]?.kind
      if (slot.mm === 30 && prev === s.kind) return ''
      if (slot.mm === 0 && prev === s.kind && row[slot.idx - 2]?.kind !== s.kind && row[slot.idx + 1]?.kind !== s.kind) return ''
      return ACTIVITIES[s.kind]?.icon || ''
    },
    cellTitle(p, slot) {
      const s = this.grid.get(p.id)?.[slot.idx]
      if (!s) return ''
      const cfg = s.kind ? ACTIVITIES[s.kind] : null
      const label = cfg ? this.$t(cfg.i18n) : ''
      const time = fmtHHMM(slotAbsMinutes(slot.idx))
      return `${time} · ${label}${s.note ? ' — ' + s.note : ''}`
    },

    onCellClick(p, slot, ev) {
      // При режиме печати не открываем
      if (document.body.classList.contains('printing')) return
      ev.stopPropagation()
      const rect = ev.target.getBoundingClientRect()
      this.editor = {
        open: true,
        x: Math.min(window.innerWidth - 360, rect.left + window.scrollX),
        y: rect.bottom + window.scrollY + 4,
        person: p,
        slotIdx: slot.idx,
      }
    },
    closeEditor() { if (this.editor.open) this.editor = { open: false, x: 0, y: 0, person: null, slotIdx: -1 } },
    setCellKind(kind) {
      const { person, slotIdx } = this.editor
      if (!person || slotIdx < 0) return
      const row = this.grid.get(person.id)
      if (!row) return
      row[slotIdx] = { kind: kind || null, note: '', source: 'manual' }
      // Мутация массива — Vue3 отслеживает через reactivity внутри Map<Array>.
      // Форсируем через переприсваивание Map (иначе computed не пересчитается).
      this.grid = new Map(this.grid)
      this.dirty.add(person.id)
      this.closeEditor()
    },

    async regenerate() {
      if (!confirm(this.$t('chpo.daily.regenerateConfirm'))) return
      await this.load({ fresh: true })
    },

    async saveAll() {
      if (!this.chpoId) return
      this.saving = true
      try {
        // Сохраняем всех, у кого есть данные (не только dirty — чтобы гарантировать persist).
        const payloads = this.personnel.map(p => ({
          chpo: this.chpoId,
          user: p.id,
          slots_json: this.rowToJson(this.grid.get(p.id) || this.emptyRow()),
        }))
        for (const pl of payloads) {
          await upsertSchedule(pl)
        }
        this.dirty = new Set()
      } catch (e) {
        console.error('DailySchedule: save failed', e)
        alert(this.$t('chpo.daily.saveError'))
      } finally { this.saving = false }
    },

    shortenName(full) {
      if (!full) return ''
      const parts = String(full).trim().split(/\s+/)
      if (parts.length <= 1) return full
      const last = parts[0]
      const initials = parts.slice(1).map(w => w[0] ? w[0] + '.' : '').join('')
      return `${last} ${initials}`
    },

    printPage() {
      document.body.classList.add('printing')
      const cleanup = () => {
        document.body.classList.remove('printing')
        window.removeEventListener('afterprint', cleanup)
      }
      window.addEventListener('afterprint', cleanup)
      setTimeout(cleanup, 30000)
      window.print()
    },

    async exportExcel() {
      const ExcelJS = (await import('exceljs')).default
      const wb = new ExcelJS.Workbook()
      wb.creator = 'Saqshy'
      wb.created = new Date((this.dateISO || '2026-01-01') + 'T00:00:00')
      const ws = wb.addWorksheet(this.$t('chpo.daily.wsName'), {
        views: [{ state: 'frozen', xSplit: 3, ySplit: 2 }],
      })

      // Шапка: 2 строки. Верхняя — 24 объединённых часа. Нижняя — пустая (можно 00/30, но по ТЗ убираем).
      const topRow = ['№', this.$t('chpo.daily.colFio'), this.$t('chpo.daily.colSleep')]
      for (let h = 0; h < 24; h++) {
        const abs = (START_HOUR + h) % 24
        topRow.push(String(abs).padStart(2, '0'))
        topRow.push('') // будет merge
      }
      const r1 = ws.addRow(topRow)
      r1.font = { bold: true, size: 10 }
      r1.alignment = { horizontal: 'center', vertical: 'middle' }
      r1.height = 22
      // Merge каждой пары
      for (let h = 0; h < 24; h++) {
        const col = 4 + h * 2
        ws.mergeCells(1, col, 1, col + 1)
      }
      // Границы
      r1.eachCell(cell => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE5E7EB' } }
        cell.border = {
          top: { style: 'thin', color: { argb: 'FF9CA3AF' } },
          left: { style: 'thin', color: { argb: 'FF9CA3AF' } },
          bottom: { style: 'thin', color: { argb: 'FF9CA3AF' } },
          right: { style: 'thin', color: { argb: 'FF9CA3AF' } },
        }
      })

      // Данные
      for (const p of this.personnel) {
        const row = this.grid.get(p.id) || this.emptyRow()
        const cells = [p.personal_number || '', p.short_name || p.full_name || '', this.sleepHours(p.id)]
        for (let i = 0; i < SLOTS_PER_DAY; i++) {
          cells.push(row[i].kind ? ACTIVITIES[row[i].kind]?.icon || '' : '')
        }
        const r = ws.addRow(cells)
        r.height = 20
        r.alignment = { horizontal: 'center', vertical: 'middle' }
        r.eachCell({ includeEmpty: true }, (cell, colNumber) => {
          cell.border = {
            top: { style: 'thin', color: { argb: 'FFE5E7EB' } },
            left: { style: 'thin', color: { argb: 'FFE5E7EB' } },
            bottom: { style: 'thin', color: { argb: 'FFE5E7EB' } },
            right: { style: 'thin', color: { argb: 'FFE5E7EB' } },
          }
          if (colNumber === 2) cell.alignment = { horizontal: 'left', vertical: 'middle' }
          // По ТЗ 03.09.2026: без заливки — только эмодзи чёрным.
          if (colNumber >= 4) {
            cell.font = { size: 12 }
          }
        })
      }

      // Ширины
      ws.getColumn(1).width = 6
      ws.getColumn(2).width = 26
      ws.getColumn(3).width = 6
      for (let i = 4; i <= 3 + SLOTS_PER_DAY; i++) ws.getColumn(i).width = 4

      // Легенда снизу
      ws.addRow([])
      ws.addRow(['УСЛОВНЫЕ ОБОЗНАЧЕНИЯ:'])
      for (const key in ACTIVITIES) {
        const cfg = ACTIVITIES[key]
        const legendRow = ws.addRow([cfg.icon, this.$t(cfg.i18n)])
        legendRow.getCell(1).font = { size: 12 }
        legendRow.getCell(1).alignment = { horizontal: 'center' }
      }

      const buf = await wb.xlsx.writeBuffer()
      const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `raspored-${this.dateISO || 'day'}.xlsx`
      document.body.appendChild(a); a.click(); document.body.removeChild(a)
      URL.revokeObjectURL(url)
    },
  },
}
</script>

<style scoped>
.daily {
  padding: 20px 24px;
  background: #F9FAFB;
  min-height: 100vh;
}
.daily__header {
  display: flex; justify-content: space-between; align-items: flex-end;
  gap: 24px; flex-wrap: wrap; margin-bottom: 16px;
}
.daily__title { margin: 0; font-size: 22px; font-weight: 800; color: #111827; }
.daily__subtitle { margin-top: 4px; color: #6B7280; font-size: 14px; }
.daily__toolbar { display: inline-flex; gap: 8px; flex-wrap: wrap; }

.btn { padding: 8px 14px; border-radius: 8px; border: 1px solid transparent; cursor: pointer; font-weight: 600; font-size: 13px; font-family: inherit; }
.btn--secondary { background: #fff; border-color: #D1D5DB; color: #374151; }
.btn--secondary:hover { background: #F3F4F6; }
.btn--secondary:disabled { opacity: 0.5; cursor: not-allowed; }
.btn--primary { background: #2F78EE; color: #fff; }
.btn--primary:hover { background: #1D4ED8; }

.daily__legend {
  display: flex; gap: 10px; flex-wrap: wrap;
  padding: 8px 12px; background: #fff; border: 1px solid #E5E7EB;
  border-radius: 10px; margin-bottom: 14px; font-size: 12px; color: #374151;
}
.legend { display: inline-flex; align-items: center; gap: 4px; }
.legend__cell {
  display: inline-block; width: 22px; height: 22px;
  text-align: center; font-size: 14px; line-height: 22px;
}
/* По ТЗ 24.09.2026: сон и служба в легенде показываются полосками,
   такими же как в таблице. */
.legend__cell--stripe { position: relative; background: transparent; }
.legend__cell--sleep::before {
  content: ''; position: absolute; left: 0; right: 0; top: 50%;
  height: 2px; background: #4B5563; transform: translateY(-50%);
}
.legend__cell--duty::before,
.legend__cell--duty::after {
  content: ''; position: absolute; left: 0; right: 0;
  height: 2px; background: #16A34A;
}
.legend__cell--duty::before { top: 34%; }
.legend__cell--duty::after  { top: 66%; transform: translateY(-100%); }
.legend__label { white-space: nowrap; }

.daily__wrap {
  background: #fff; border: 1px solid #D1D5DB;
  border-radius: 10px; overflow: auto;
}
.daily__table { border-collapse: collapse; font-size: 11px; width: 100%; }
.daily__table thead th {
  position: sticky; top: 0; z-index: 2;
  background: #E5E7EB; padding: 6px 4px;
  border: 1px solid #9CA3AF; text-align: center;
  font-weight: 700; color: #111827; font-size: 12px;
}
.daily__col--num, .daily__col--fio, .daily__col--sleep {
  background: #F3F4F6; padding: 4px 8px;
  border: 1px solid #9CA3AF; white-space: nowrap;
}
.daily__col--num { width: 48px; text-align: center; font-weight: 700; }
.daily__col--fio { min-width: 200px; text-align: left; }
.daily__col--sleep { width: 40px; text-align: center; font-weight: 700; color: #1E40AF; }

.daily__badge {
  display: inline-block; margin-left: 6px; padding: 1px 6px;
  background: #FEE2E2; color: #991B1B; border-radius: 4px;
  font-size: 10px; font-weight: 700;
}

.daily__col--hour {
  min-width: 32px; padding: 4px 2px;
  border-left: 1px solid #9CA3AF;
}
.daily__col--hour-major { border-left-width: 2px; border-left-color: #374151; }

.daily__cell {
  min-width: 16px; height: 26px; padding: 0;
  border: 1px solid #E5E7EB;
  text-align: center; vertical-align: middle;
  cursor: pointer;
  transition: outline 0.1s;
  position: relative;                 /* контейнер для абсолютной иконки */
}
.daily__cell:hover { outline: 2px solid #2F78EE; outline-offset: -2px; }
.daily__cell.is-half { border-right: 1px dashed #D1D5DB; }
.daily__cell:nth-child(2n+3) { border-right: 1px solid #9CA3AF; }

/* По ТЗ 22.09.2026: длинные блоки одного kind = сплошная полоса.
   Полностью убираем боковую границу когда справа/слева продолжение того же
   блока — иначе цветной фон соседей разрывается 1-2px мёртвого пространства. */
.daily__cell.is-continues-right { border-right: 0 !important; }
.daily__cell.is-continues-left  { border-left:  0 !important; }

/* По ТЗ 22.09.2026: иконка стоит по центру ПАРЫ полуслотов (одного часа),
   а не в первой её ячейке. Абсолютное позиционирование + translate(50%)
   даёт «переезд» иконки на границу пары — визуально она в середине двух
   квадратов и не мешает клику по получасу. */
.daily__cell-icon {
  font-size: 13px; line-height: 1;
  position: absolute;
  left: 100%; top: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
  z-index: 2;
  white-space: nowrap;
}
.legend__cell { font-size: 12px; }

/* По ТЗ 24.09.2026: сон — одна тонкая полоса по центру ячейки, служба —
   две параллельные полосы (как топографические условные знаки). Фон ячейки
   остаётся белым, сплошной заливки нет. Классы is-continues-left/right c
   border:0 обеспечивают, что соседние ячейки сливаются в одну непрерывную
   линию сквозь весь блок. */
.daily__cell.is-sleep::before {
  content: '';
  position: absolute;
  left: 0; right: 0; top: 50%;
  height: 2px;
  background: #4B5563;
  transform: translateY(-50%);
  z-index: 1;
}
.daily__cell.is-duty::before,
.daily__cell.is-duty::after {
  content: '';
  position: absolute;
  left: 0; right: 0;
  height: 2px;
  background: #16A34A;
  z-index: 1;
}
.daily__cell.is-duty::before { top: 34%; }
.daily__cell.is-duty::after  { top: 66%; transform: translateY(-100%); }
/* На границах блока внутренние вертикальные линии убираются:
   is-continues-left убирает левую, is-continues-right — правую (см. выше). */

.cell-editor {
  position: absolute; z-index: 100;
  background: #fff; border: 1px solid #D1D5DB; border-radius: 8px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.15); padding: 8px;
  min-width: 320px; max-width: 420px;
}
.cell-editor__title { font-size: 12px; color: #6B7280; margin-bottom: 6px; }
.cell-editor__grid { display: grid; grid-template-columns: 1fr 1fr; gap: 4px; }
.cell-editor__opt {
  padding: 4px 6px; border-radius: 4px; border: 1px solid #E5E7EB;
  font-size: 11px; text-align: left; cursor: pointer; font-family: inherit;
}
.cell-editor__opt:hover { filter: brightness(1.05); }
.cell-editor__opt--clear { grid-column: 1 / -1; background: #F3F4F6; color: #6B7280; text-align: center; }
.cell-editor__hint { margin-top: 6px; font-size: 10px; color: #9CA3AF; text-align: center; }

.daily__loading, .daily__empty { padding: 40px; text-align: center; color: #6B7280; }
.daily__empty .hint { margin-top: 8px; font-size: 13px; }

.print-only { display: none; }
.daily__print-header .print__unit { font-size: 14px; }
.daily__print-header .print__title { font-size: 18px; font-weight: 800; margin: 6px 0; }
.daily__print-header .print__date { font-size: 12px; color: #444; }

@media print {
  @page { size: A4 landscape; margin: 8mm; }
  body { background: #fff; }
  .no-print { display: none !important; }
  .print-only { display: block; }
  .daily { padding: 0; background: #fff; min-height: 0; }
  .daily__wrap { border: 0; overflow: visible; page-break-inside: auto; }
  .daily__table { font-size: 8px; page-break-inside: auto; }
  .daily__table thead { display: table-header-group; }
  .daily__table tr { page-break-inside: avoid; page-break-after: auto; }
  .daily__cell { height: 16px; }
  .daily__col--fio { min-width: 100px; font-size: 9px; }
  .daily__col--num { width: 28px; font-size: 9px; }
  .daily__cell,
  .daily__col--num, .daily__col--fio, .daily__col--sleep {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .daily__legend { flex-wrap: wrap; font-size: 8px; padding: 4px; }
}
</style>

<!--
  По ТЗ 03.09.2026: при печати A4 landscape нужен только контент — без
  сайдбара и хедера DefaultLayout. Стиль не-scoped.
-->
<style>
@media print {
  body.printing .layout__sidebar,
  body.printing header.header,
  body.printing .header { display: none !important; }
  body.printing .layout__main,
  body.printing .layout__content { margin: 0 !important; padding: 0 !important; width: 100% !important; max-width: 100% !important; }
  body.printing .layout { display: block !important; }
}
</style>
